import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AvisoDeStatus, GuardaDeVersaoDoEsquema, type InfoDeAtualizacao } from "@pps/ui/components/sistema";
import { textosDoAvisoDeStatus, urlDaPaginaDeStatusNoNavegador } from "@pps/ui/lib/status-da-plataforma";

import { Sentry } from "@pps/observability";
import { ESQUEMA_ESPERADO, ROTULO_DA_VERSAO } from "@/lib/versao";
import { supabase } from "@/integrations/supabase/client";

const consultar = () => supabase.rpc("app_schema_version");

function aoEntrar(info: InfoDeAtualizacao) {
  // Página = app trocado (banco atrás/função ausente, ou falha persistente na partida);
  // faixa = "Conexão instável" por cima do app em uso.
  const pagina = info.tela === "pagina";
  Sentry.captureMessage(pagina ? "versao-esquema.em-atualizacao" : "versao-esquema.conexao-instavel", {
    level: "warning",
    tags: { feature: "versao-esquema", action: pagina ? "entrar" : "instavel", motivo: info.motivo },
    extra: { ...info, versaoDoApp: ROTULO_DA_VERSAO },
  });
}

/**
 * "Sistema em atualização" / faixa "Conexão instável" (packages/ui, GuardaDeVersaoDoEsquema)
 * + banner de status da plataforma (AvisoDeStatus, só em PROD — doc: banner-de-status.md),
 * irmão da guarda para aparecer também quando ela troca o app pela página de atualização.
 */
export function SaudeDoEsquema({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  // Lida na montagem (o override de teste no localStorage vale ao recarregar a página).
  const [urlDoStatus] = useState(() => urlDaPaginaDeStatusNoNavegador(import.meta.env.VITE_APP_ENV));
  const textosDoStatus = useMemo(() => textosDoAvisoDeStatus(t), [t]);
  const textos = useMemo(
    () => ({
      titulo: t("sistema.atualizacao.titulo"),
      descricao: t("sistema.atualizacao.descricao"),
      proximaTentativa: (segundos: number) => t("sistema.atualizacao.proximaTentativa", { segundos }),
      verificando: t("sistema.atualizacao.verificando"),
      tentarAgora: t("sistema.atualizacao.tentarAgora"),
    }),
    [t],
  );
  return (
    <>
      <AvisoDeStatus url={urlDoStatus} textos={textosDoStatus} />
      <GuardaDeVersaoDoEsquema
        esperada={ESQUEMA_ESPERADO}
        consultar={consultar}
        textos={textos}
        textoFaixa={t("sistema.instavel.texto")}
        aoEntrar={aoEntrar}
        rodape={ROTULO_DA_VERSAO}
      >
        {children}
      </GuardaDeVersaoDoEsquema>
    </>
  );
}
